import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AnalyticDistributionModelContext } from '../../../apps/configuration/accounting-finance/analytic-distribution-models/new-modal/new-modal';
import {
  AnalyticDistributionModelRecord,
  AnalyticDistributionOwnerOption,
  AnalyticDistributionService,
} from '../../../core/services/analytic-distribution';

interface DistributionLine {
  analyticAccountId: string;
  percentage: number;
}

@Component({
  selector: 'app-analytic-distribution-input',
  standalone: false,
  templateUrl: './analytic-distribution-input.html',
  styleUrl: './analytic-distribution-input.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AnalyticDistributionInput), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => AnalyticDistributionInput), multi: true },
  ],
})
export class AnalyticDistributionInput implements ControlValueAccessor, Validator, OnChanges, OnInit, OnDestroy {
  @Input() required = false;
  @Input() partnerId: string | null = null;
  @Input() productId: string | null = null;
  @Input() productCategoryId: string | null = null;
  @Input() accountCode: string | null = null;
  @Input() defaultDistribution: Record<string, number> | null = null;
  @Input() amount = 0;
  @Input() transactionDate: string | null = null;
  @Input() triggerClass = 'form-control';

  accounts: AnalyticDistributionOwnerOption[] = [];
  lines: DistributionLine[] = [];
  matchedModelName = '';
  loadError = '';
  disabled = false;
  dropdownOpen = false;
  modelModalOpen = false;
  budgetExceeded = false;
  budgetCheckError = '';
  budgetCheckPending = false;

  private models: AnalyticDistributionModelRecord[] = [];
  private valueWritten = false;
  private userChanged = false;
  private suggestedDistribution = false;
  private availabilityTimer?: ReturnType<typeof setTimeout>;
  private availabilitySequence = 0;
  private lastBudgetWarning = '';
  private readonly modelSubscription: Subscription;
  private onChange: (value: Record<string, number>) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor(
    private readonly distributions: AnalyticDistributionService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.modelSubscription = this.distributions.modelChanges$.subscribe((record) => {
      this.models = [...this.models.filter((candidate) => candidate.model.id !== record.model.id), record];
      if (!this.userChanged && this.lines.length === 0) this.applySuggestedDistribution();
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      const [options, response] = await Promise.all([
        firstValueFrom(this.distributions.options()),
        firstValueFrom(this.distributions.models()),
      ]);
      this.accounts = options.analytic_accounts ?? [];
      this.models = response.records ?? [];
      this.applySuggestedDistribution();
      this.cdr.markForCheck();
    } catch {
      this.loadError = 'Analytic accounts and automatic distribution models could not be loaded.';
      this.onValidatorChange();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.availabilitySequence++;
    this.modelSubscription.unsubscribe();
    if (this.availabilityTimer) clearTimeout(this.availabilityTimer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const contextChanged = ['partnerId', 'productId', 'productCategoryId', 'accountCode', 'defaultDistribution']
      .some((key) => Boolean(changes[key]));
    if (contextChanged && this.suggestedDistribution && !this.userChanged) {
      this.lines = [];
      this.matchedModelName = '';
      this.suggestedDistribution = false;
    }
    this.applySuggestedDistribution();
    if (changes['amount'] || changes['transactionDate']) this.scheduleBudgetCheck();
  }

  writeValue(value: Record<string, number> | null | undefined): void {
    this.valueWritten = true;
    const entries = Object.entries(value ?? {});
    this.lines = entries.map(([analyticAccountId, percentage]) => ({
      analyticAccountId,
      percentage: Number(percentage),
    }));
    this.userChanged = entries.length > 0;
    this.suggestedDistribution = false;
    this.matchedModelName = '';
    this.applySuggestedDistribution();
    this.scheduleBudgetCheck();
  }

  registerOnChange(fn: (value: Record<string, number>) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.dropdownOpen = false;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.loadError && this.lines.length > 0) {
      return { analyticOptions: { message: this.loadError } };
    }
    if (this.required && this.lines.length === 0) {
      return { analyticRequired: { message: 'Add an analytic distribution.' } };
    }
    if (this.lines.some((line) => !line.analyticAccountId || line.percentage <= 0 || line.percentage > 100)) {
      return { analyticLine: { message: 'Every analytic line needs an account and a percentage between 0 and 100.' } };
    }
    if (new Set(this.lines.map((line) => line.analyticAccountId)).size !== this.lines.length) {
      return { analyticDuplicate: { message: 'An analytic account can appear only once.' } };
    }
    if (this.lines.length > 0 && Math.abs(this.total - 100) > 0.0001) {
      return { analyticTotal: { message: 'Analytic distribution percentages must total exactly 100%.' } };
    }
    if (this.budgetExceeded) {
      return { analyticBudgetExceeded: { message: 'This amount exceeds the remaining analytic budget.' } };
    }
    if (this.budgetCheckError || this.budgetCheckPending) {
      return { analyticBudgetCheck: { message: this.budgetCheckError || 'Wait for the analytic budget check to finish.' } };
    }
    return null;
  }

  get total(): number {
    return this.lines.reduce((sum, line) => sum + Number(line.percentage || 0), 0);
  }

  get modelContext(): AnalyticDistributionModelContext {
    return {
      partnerId: this.partnerId,
      productId: this.productId,
      productCategoryId: this.productCategoryId,
      accountCode: this.accountCode,
    };
  }

  get distributionValue(): Record<string, number> {
    return this.lines.reduce<Record<string, number>>((distribution, line) => {
      if (line.analyticAccountId) distribution[line.analyticAccountId] = Number(line.percentage || 0);
      return distribution;
    }, {});
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.dropdownOpen = !this.dropdownOpen;
    this.onTouched();
  }

  setDropdownOpen(open: boolean): void {
    if (this.modelModalOpen && !open) return;
    this.dropdownOpen = open;
  }

  closeDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropdownOpen = false;
    this.onTouched();
  }

  openAnalyticPlanForm(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    window.open(
      `${window.location.origin}/configuration/accounting-finance/analytic-plans/new`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  addLine(): void {
    this.userChanged = true;
    this.suggestedDistribution = false;
    this.matchedModelName = '';
    this.lines = [...this.lines, { analyticAccountId: '', percentage: this.lines.length === 0 ? 100 : 0 }];
    this.emitValue();
  }

  removeLine(index: number): void {
    this.userChanged = true;
    this.suggestedDistribution = false;
    this.matchedModelName = '';
    this.lines = this.lines.filter((_line, lineIndex) => lineIndex !== index);
    this.emitValue();
  }

  lineChanged(): void {
    this.userChanged = true;
    this.suggestedDistribution = false;
    this.matchedModelName = '';
    this.emitValue();
  }

  openNewModel(): void {
    this.modelModalOpen = true;
    this.dropdownOpen = true;
  }

  onModelModalOpenChange(open: boolean): void {
    this.modelModalOpen = open;
  }

  onModelSaved(record: AnalyticDistributionModelRecord): void {
    this.distributions.upsertModel(record);
    this.modelModalOpen = false;
  }

  accountLabel(accountId: string): string {
    const account = this.accounts.find((candidate) => candidate.id === accountId);
    return account ? `${account.code ? `${account.code} · ` : ''}${account.label}` : `Unknown account (${accountId})`;
  }

  hasAccount(accountId: string): boolean {
    return this.accounts.some((candidate) => candidate.id === accountId);
  }

  private applySuggestedDistribution(): void {
    if (!this.valueWritten || this.userChanged || this.lines.length > 0) return;
    if (this.defaultDistribution && Object.keys(this.defaultDistribution).length > 0) {
      this.lines = Object.entries(this.defaultDistribution).map(([analyticAccountId, percentage]) => ({
        analyticAccountId,
        percentage: Number(percentage),
      }));
      this.matchedModelName = 'Product default';
      this.suggestedDistribution = true;
      this.emitValue(false);
      return;
    }
    const model = [...this.models]
      .filter((candidate) => candidate.model.active && this.matches(candidate))
      .sort((left, right) => left.model.sequence - right.model.sequence)[0];
    if (!model) return;
    this.lines = model.lines.map((line) => ({
      analyticAccountId: line.analytic_account_id,
      percentage: Number(line.percentage),
    }));
    this.matchedModelName = model.model.name;
    this.suggestedDistribution = true;
    this.emitValue(false);
  }

  private matches(candidate: AnalyticDistributionModelRecord): boolean {
    const model = candidate.model;
    return (!model.partner_id || model.partner_id === this.partnerId)
      && (!model.product_id || model.product_id === this.productId)
      && (!model.product_category_id || model.product_category_id === this.productCategoryId)
      && (!model.account_prefix || (this.accountCode ?? '').startsWith(model.account_prefix));
  }

  private emitValue(markTouched = true): void {
    this.onChange(this.distributionValue);
    if (markTouched) this.onTouched();
    this.onValidatorChange();
    this.scheduleBudgetCheck();
  }

  private scheduleBudgetCheck(): void {
    this.availabilitySequence++;
    if (this.availabilityTimer) clearTimeout(this.availabilityTimer);
    this.budgetCheckError = '';
    if (this.amount <= 0 || Object.keys(this.distributionValue).length === 0) {
      this.budgetExceeded = false;
      this.budgetCheckPending = false;
      this.lastBudgetWarning = '';
      this.onValidatorChange();
      return;
    }
    this.budgetCheckPending = true;
    this.onValidatorChange();
    this.availabilityTimer = setTimeout(() => void this.checkBudgetAvailability(), 300);
  }

  private async checkBudgetAvailability(): Promise<void> {
    const sequence = ++this.availabilitySequence;
    try {
      const result = await firstValueFrom(
        this.distributions.checkBudgetAvailability(this.distributionValue, Number(this.amount), this.transactionDate),
      );
      if (sequence !== this.availabilitySequence) return;
      this.budgetCheckPending = false;
      this.budgetExceeded = !result.allowed;
      this.onValidatorChange();
      this.cdr.markForCheck();
      if (!result.allowed) {
        const first = result.exceeded?.[0];
        const warning = first
          ? `${first.budget_name ?? 'Analytic budget'} exceeded for ${first.analytic_account_name ?? 'the selected account'}; ${Number(first.remaining_amount ?? 0).toFixed(2)} remains.`
          : 'This analytic distribution exceeds the remaining budget.';
        if (warning !== this.lastBudgetWarning) {
          this.lastBudgetWarning = warning;
          this.snackBar.open(warning, 'Dismiss', { duration: 7000, panelClass: ['snackbar-error'] });
        }
      } else {
        this.lastBudgetWarning = '';
      }
    } catch {
      if (sequence !== this.availabilitySequence) return;
      this.budgetExceeded = false;
      this.budgetCheckPending = false;
      this.budgetCheckError = 'The analytic budget could not be checked. Change the amount or retry before saving.';
      this.onValidatorChange();
      this.cdr.markForCheck();
    }
  }
}
